import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Activity, AlertTriangle, CheckCircle2, Clock, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * OTP Health dashboard tile.
 *
 * Surfaces signup-OTP dispatch + delivery health so operators can:
 *   • Tell at a glance if Resend is fast (us→Resend) or slow
 *   • Track send→verify lag as a leading indicator of inbox-side delays
 *     (i.e. when Gmail/Outlook reputation starts drifting before users complain)
 *   • See failed sends and the Resend error reason
 */
export default function AdminOtpHealth() {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [probeEmail, setProbeEmail] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/otp/health", { params: { hours } });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load OTP health");
    }
  };

  useEffect(() => { load(); }, [hours]); // eslint-disable-line react-hooks/exhaustive-deps

  const runProbe = async () => {
    if (!probeEmail) {
      toast.error("Enter an email to probe");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email/diagnose", { to: probeEmail });
      toast.success(`Resend RTT ${data.resendRoundTripMs}ms — ${data.interpretation}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Probe failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <div className="text-foreground/60 text-sm" data-testid="otp-health-loading">Loading OTP health…</div>;
  }

  const fmtMs = (n) => (n == null ? "—" : `${n.toLocaleString()}ms`);
  const fmtPct = (n) => (n == null ? "—" : `${Math.round(n * 100)}%`);
  const quotaActive = data.quota?.active;
  const em = data.essentialMode || {};
  const emActive = em.active === true || em === true;  // back-compat with old payload
  const emSource = em.source || "off";
  const emExpiresAt = em.expiresAt;

  const toggleEssential = async (next) => {
    try {
      await api.post("/admin/email/essential-mode", { active: next });
      toast.success(next ? "Essential mode enabled" : "Essential mode disabled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const dispatchRTTBadge =
    data.dispatchRTTms.p50 == null ? "neutral"
    : data.dispatchRTTms.p50 < 800 ? "good"
    : data.dispatchRTTms.p50 < 2000 ? "warn"
    : "bad";
  const verifyLagBadge =
    data.sendToVerifyMs.p50 == null ? "neutral"
    : data.sendToVerifyMs.p50 < 30_000 ? "good"
    : data.sendToVerifyMs.p50 < 120_000 ? "warn"
    : "bad";
  const badgeCls = {
    good: "text-emerald-400 border-emerald-500/30",
    warn: "text-amber-400 border-amber-500/40",
    bad: "text-rose-400 border-rose-500/40",
    neutral: "text-foreground/50 border-secondary/20",
  };

  return (
    <div className="space-y-6" data-testid="admin-otp-health">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mystic flex items-center gap-2"><Activity className="w-3 h-3" /> OTP Health</div>
          <h2 className="font-serif text-3xl mt-2">Signup verification telemetry</h2>
          <p className="text-foreground/60 text-sm mt-1 max-w-2xl">
            Dispatch RTT measures how quickly Resend accepts each OTP. Send→verify lag is a
            proxy for actual inbox arrival — when this drifts upward, Gmail/Outlook reputation
            is shifting before users notice.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="bg-card border border-secondary/20 text-foreground px-3 py-2 text-xs uppercase tracking-[0.18em]"
            data-testid="otp-health-window"
          >
            <option value={1}>Last 1h</option>
            <option value={24}>Last 24h</option>
            <option value={72}>Last 3d</option>
            <option value={168}>Last 7d</option>
          </select>
          <button
            onClick={load}
            className="px-3 py-2 text-xs uppercase tracking-[0.18em] border border-secondary/30 text-secondary hover:bg-secondary/10"
            data-testid="otp-health-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {quotaActive && (
        <div
          className="border border-rose-500/60 bg-rose-500/10 p-4 flex items-start gap-3"
          data-testid="otp-quota-banner"
        >
          <AlertTriangle className="w-5 h-5 text-rose-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-rose-200 text-sm font-semibold uppercase tracking-[0.2em]">
              Resend daily quota exceeded — OTPs are failing right now
            </div>
            <div className="text-rose-100/80 text-sm mt-1">
              {data.quota.exceededLast5Min} send(s) blocked in the last 5 min, {data.quota.exceededInWindow} in this window.
              Upgrade plan at <a href="https://resend.com/settings/billing" target="_blank" rel="noreferrer" className="underline">resend.com/settings/billing</a> OR
              set <code className="bg-rose-950/50 px-1">EMAIL_ESSENTIAL_MODE=true</code> in backend env to suppress non-OTP sends and stretch headroom.
            </div>
          </div>
        </div>
      )}

      {data.essentialMode && (emActive || emSource !== "off") && (
        <div
          className="border border-amber-500/60 bg-amber-500/10 p-3 text-amber-100 text-sm flex items-center gap-3 flex-wrap"
          data-testid="essential-mode-banner"
        >
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="flex-1 min-w-[280px]">
            <b>Essential mode is ON</b> ({emSource}) — only OTP / password-reset / subscription confirmations
            are being sent. Welcome drips + weekly digests are suppressed.
            {emExpiresAt && (
              <> Auto-reverts <span className="underline">{new Date(emExpiresAt).toLocaleString()}</span>.</>
            )}
          </span>
          {emSource !== "env" && (
            <button
              onClick={() => toggleEssential(!emActive)}
              className="px-3 py-1.5 text-xs uppercase tracking-[0.2em] border border-amber-500/40 text-amber-100 hover:bg-amber-500/20"
              data-testid="essential-mode-toggle"
            >
              {emActive ? "Disable now" : "Re-enable"}
            </button>
          )}
        </div>
      )}

      {!emActive && emSource === "off" && (
        <div className="text-foreground/50 text-xs flex items-center gap-2">
          <span>Essential mode: <b className="text-foreground/70">off</b></span>
          <button
            onClick={() => toggleEssential(true)}
            className="ml-2 px-2 py-1 text-[10px] uppercase tracking-[0.22em] border border-secondary/20 hover:bg-secondary/10"
            data-testid="essential-mode-enable-btn"
          >
            Enable manually
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={Mail} label="Sends" value={data.totals.sends} subtitle={`${data.totals.ok} ok · ${data.totals.failed} failed`} testid="otp-tile-sends" />
        <Tile icon={CheckCircle2} label="Verify rate" value={fmtPct(data.totals.verifyRate)} subtitle={`${data.totals.verified} verified`} testid="otp-tile-verify-rate" />
        <Tile
          icon={Clock}
          label="Dispatch RTT p50 / p95"
          value={`${fmtMs(data.dispatchRTTms.p50)} / ${fmtMs(data.dispatchRTTms.p95)}`}
          subtitle={`avg ${fmtMs(data.dispatchRTTms.avg)} · n=${data.dispatchRTTms.samples}`}
          badge={dispatchRTTBadge}
          testid="otp-tile-dispatch-rtt"
        />
        <Tile
          icon={AlertTriangle}
          label="Send→verify p50 / p95"
          value={`${fmtMs(data.sendToVerifyMs.p50)} / ${fmtMs(data.sendToVerifyMs.p95)}`}
          subtitle={`n=${data.sendToVerifyMs.samples} verified`}
          badge={verifyLagBadge}
          testid="otp-tile-verify-lag"
        />
      </div>

      <div className="border border-secondary/15 bg-card/40 p-4">
        <div className="label-mystic mb-3">Live probe</div>
        <div className="flex flex-wrap gap-2">
          <input
            type="email"
            placeholder="probe@yourdomain.com"
            value={probeEmail}
            onChange={(e) => setProbeEmail(e.target.value)}
            className="flex-1 min-w-[240px] bg-background border border-secondary/20 text-foreground px-3 py-2 text-sm"
            data-testid="otp-probe-email-input"
          />
          <button
            onClick={runProbe}
            disabled={busy}
            className="px-4 py-2 text-xs uppercase tracking-[0.22em] bg-secondary text-background hover:bg-secondary/90 disabled:opacity-50"
            data-testid="otp-probe-run-btn"
          >
            {busy ? "Probing…" : "Run probe"}
          </button>
        </div>
        <p className="text-foreground/50 text-xs mt-2">
          Sends a real Resend email and reports the API round-trip time. Useful to confirm dispatch is
          fast when investigating user-reported delays.
        </p>
      </div>

      <div className="border border-secondary/15">
        <div className="px-4 py-3 border-b border-secondary/10 flex items-center justify-between">
          <div className="label-mystic">Recent sends ({data.recent.length})</div>
          <span className={`text-[10px] uppercase tracking-[0.22em] border px-2 py-1 ${badgeCls[verifyLagBadge]}`}>
            Inbox health · {verifyLagBadge}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="otp-health-table">
            <thead className="text-foreground/50 text-[10px] uppercase tracking-[0.22em]">
              <tr>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Dispatched</th>
                <th className="text-right px-4 py-2">RTT</th>
                <th className="text-right px-4 py-2">→verify</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((r, i) => (
                <tr key={i} className="border-t border-secondary/10">
                  <td className="px-4 py-2 font-mono text-xs">{r.email}</td>
                  <td className="px-4 py-2 text-xs text-foreground/60">
                    {r.dispatchedAt ? new Date(r.dispatchedAt).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{fmtMs(r.resendRTTms)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {r.sendToVerifyMs != null ? fmtMs(r.sendToVerifyMs) : "—"}
                  </td>
                  <td className="px-4 py-2">
                    {r.ok === false ? (
                      <span className="text-rose-400 text-xs" title={r.errorMessage}>Failed</span>
                    ) : r.verifiedAt ? (
                      <span className="text-emerald-400 text-xs">Verified</span>
                    ) : (
                      <span className="text-foreground/40 text-xs">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
              {data.recent.length === 0 && (
                <tr><td colSpan={5} className="text-center text-foreground/40 text-sm py-8">No OTP sends in this window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, subtitle, badge, testid }) {
  const ring = badge === "good" ? "border-emerald-500/30"
            : badge === "warn" ? "border-amber-500/40"
            : badge === "bad" ? "border-rose-500/40"
            : "border-secondary/15";
  return (
    <div className={`border bg-card/40 p-4 ${ring}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-foreground/50">
        <Icon className="w-3 h-3" />
        <span className="text-[10px] uppercase tracking-[0.22em]">{label}</span>
      </div>
      <div className="font-serif text-2xl mt-2 text-foreground">{value}</div>
      {subtitle && <div className="text-foreground/40 text-xs mt-1">{subtitle}</div>}
    </div>
  );
}
